import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormComponentProps } from '../../global-forms/GlobalForms.types';
import { useForm } from 'react-hook-form';
import { CreateRoleDto, QueryFactory } from '@lmt/shared-types';
import {
  getGroupKeysByPermissions,
  getPermissionsByGroupKeys,
  PermissionGroupKey,
  permissionGroups,
} from '../common';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import {
  convertToErrorString,
  useErrorHandler,
} from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useModal } from '../../common/ui-kit/modal/useModal';
import localStyles from './RoleForm.module.scss';
import styles from '@lmt/unicorn-shared/app/components/global-forms/Content.module.scss';
import { Input } from '../../common/ui-kit/inputs/Input';
import { Field } from '../../common/Field';
import { SubmitButton } from '../../common/ui-kit/buttons/SubmitButton';
import { nameofFactory } from '@lmt/unicorn-shared/app/helpers/nameof';
import { requiredRule } from '@lmt/unicorn-shared/app/helpers/form/react-hook-form-helper';
import { snakeCase } from 'lodash';
import { useTranslation } from 'react-i18next';
import { UniLink } from '../../common/ui-kit/buttons/UniLink';
import { ButtonColor } from '../../common/ui-kit/buttons/Button';
import { CheckBox } from '../../common/ui-kit/CheckBox';
import { createId } from '@lmt/unicorn-shared/app/helpers/create-id';
import TipIcon from '@lmt/unicorn-shared/app/icons/tip.svg?react';
import { AppTooltip } from '../../common/ui-kit/tooltip/AppTooltip';
import { localizePermission } from '@lmt/unicorn-shared/app/helpers/localization/translations';

const nameof = nameofFactory<CreateRoleDto>();

interface SubOption {
  groupKey: PermissionGroupKey;
  displayValue: string;
  disabled?: boolean;
  permissions: string[];
}

type Option = SubOption & {
  subOptions?: SubOption[];
};

export interface RoleProps {
  roleId?: number;
}

export const RoleFormWithGroups: FC<FormComponentProps & RoleProps> = (
  props,
) => {
  const { formType, formId, tryCompleteForm, roleId, loadBlocking } = props;
  const i18next = useScopedTranslation('roles.form');
  const i18nextGroups = useScopedTranslation('roles.groups');
  const i18n = useTranslation();
  const modals = useModal();
  const [roleGroupKeys, setRoleGroupKeys] = useState<PermissionGroupKey[]>([]);
  const [isDefault, setDefault] = useState<boolean>(!!props.roleId);
  const [defaultRoleName, setDefaultRoleName] = useState<string>('');

  const {
    register,
    reset,
    handleSubmit,
    formState,
    setError,
    setValue,
    watch,
  } = useForm<CreateRoleDto>({
    shouldFocusError: true,
    criteriaMode: 'all',
  });

  useEffect(() => {
    async function getData() {
      if (roleId) {
        const roleRequest = await QueryFactory.RoleClient.getRole(roleId);
        if (isDefault) {
          setDefaultRoleName(roleRequest.name);
        }
        if (!roleRequest.isDefault && roleRequest.isEditable) {
          setDefault(false);
        }
        if (roleRequest.permissions) {
          const groupKeys = getGroupKeysByPermissions(roleRequest.permissions);
          setRoleGroupKeys(groupKeys);
        }
        setValue('name', roleRequest.name);
      } else {
        setDefault(false);
      }
    }

    loadBlocking(formId, i18next.t('loading'), () => getData());
  }, []);

  const roleName = watch('name');

  const submitForm = useErrorHandler<CreateRoleDto>(
    useCallback(
      async (data: CreateRoleDto) => {
        data.permissions = getPermissionsByGroupKeys(roleGroupKeys);
        await tryCompleteForm(formType, formId, async () => {
          try {
            if (roleId) {
              await QueryFactory.RoleClient.editRole(roleId, data);
            } else {
              await QueryFactory.RoleClient.createRole(data);
            }
          } catch (err) {
            await modals.showError({ text: convertToErrorString(err) });
          }
        });
      },
      [roleGroupKeys, formType, formId, tryCompleteForm, modals],
    ),
    setError,
    reset,
  );

  const allEnabledGroupKeys = useMemo(() => {
    return permissionGroups
      .filter((x) => !x.roleName || x.roleName === defaultRoleName)
      .map((x) => x.key);
  }, [defaultRoleName]);

  const permissionGroupsOptions = useMemo(() => {
    const options = permissionGroups
      .filter(
        (option) => !option.access && allEnabledGroupKeys.includes(option.key),
      )
      .map((option) => {
        const subOptions = permissionGroups
          .filter((subOption) => subOption.access === option.key)
          .map((subOption) => {
            return {
              groupKey: subOption.key,
              displayValue: i18nextGroups.t(subOption.name as any) as any,
              disabled: !roleGroupKeys.includes(option.key) || isDefault,
              permissions: subOption.permissions
                .map((x) => localizePermission(x))
                .sort((a, b) => a.localeCompare(b)),
            };
          })
          .sort((a, b) => a.displayValue.localeCompare(b.displayValue));
        return {
          groupKey: option.key,
          displayValue: i18nextGroups.t(option.name as any) as any,
          disabled: isDefault,
          subOptions: subOptions,
          permissions: option.permissions
            .map((x) => localizePermission(x))
            .sort((a, b) => a.localeCompare(b)),
        };
      })
      .sort((a, b) => a.displayValue.localeCompare(b.displayValue));
    return options;
  }, [roleGroupKeys, allEnabledGroupKeys, isDefault, roleName]);

  return (
    <form
      className={localStyles.container}
      onSubmit={handleSubmit(submitForm.handler)}
    >
      <div className={styles.dataContainer}>
        <Field title={i18next.t('name')} required={!isDefault}>
          {isDefault ? (
            <Input
              value={i18n.t(
                `roles.list.default_roles.${snakeCase(roleName)}`,
                '',
              )}
              errorText={formState.errors.name?.message}
              disabled
            />
          ) : (
            <Input
              {...register(nameof('name'), requiredRule())}
              errorText={formState.errors.name?.message}
              autoFocus={true}
            />
          )}
        </Field>
        <Field
          titleClassName={styles.documentsTitle}
          className={styles.field}
          title={i18next.t('permissions')}
        >
          {!isDefault && (
            <div className={localStyles.optionsContainer}>
              <UniLink
                color={ButtonColor.Primary}
                title={i18next.t('check_all')}
                className={localStyles.link}
                disabled={allEnabledGroupKeys.length === roleGroupKeys.length}
                onClick={() => setRoleGroupKeys(allEnabledGroupKeys)}
              />
              <UniLink
                color={ButtonColor.Primary}
                title={i18next.t('check_none')}
                className={localStyles.link}
                disabled={roleGroupKeys.length === 0}
                onClick={() => setRoleGroupKeys([])}
              />
            </div>
          )}
          <div>
            {permissionGroupsOptions.map((option) => {
              return (
                <div key={option.groupKey} className={localStyles.options}>
                  <RoleGroupOption
                    option={option}
                    selectedValues={roleGroupKeys}
                    setSelectedValues={setRoleGroupKeys}
                  />
                  <div className={localStyles['suboptions-container']}>
                    {option.subOptions.map((subOption) => {
                      return (
                        <RoleGroupOption
                          key={subOption.groupKey}
                          option={subOption}
                          selectedValues={roleGroupKeys}
                          setSelectedValues={setRoleGroupKeys}
                        />
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </Field>
      </div>
      {!isDefault && (
        <div className={styles.submitContainer}>
          <SubmitButton
            submitting={formState.isSubmitting}
            className={styles.submit}
            title={
              roleId ? i18next.t('save_button') : i18next.t('create_button')
            }
          />
        </div>
      )}
    </form>
  );
};

type OptionProps = {
  option: Option;
  selectedValues: PermissionGroupKey[];
  setSelectedValues: (values: PermissionGroupKey[]) => void;
};
const RoleGroupOption: React.FC<OptionProps> = (props) => {
  const { option, selectedValues, setSelectedValues } = props;

  const onChange = useCallback(
    (e: React.FormEvent<HTMLInputElement>, selectedOption: Option) => {
      const foundOption = selectedOption;
      if (e.currentTarget.checked) {
        setSelectedValues([...selectedValues, selectedOption.groupKey]);
      } else {
        const suboptionsKeys =
          selectedOption.subOptions?.map((x) => x.groupKey) ?? [];
        setSelectedValues(
          selectedValues.filter(
            (x) => x !== selectedOption.groupKey && !suboptionsKeys.includes(x),
          ),
        );
      }
    },
    [selectedValues],
  );

  return (
    <div className={localStyles['option-container']}>
      <CheckBox
        title={option.displayValue}
        checked={selectedValues?.includes(option.groupKey)}
        name={createId() + option.groupKey}
        disabled={option.disabled}
        onChange={(e) => {
          onChange(e, option);
        }}
      />
      <AppTooltip
        key={option.groupKey}
        title={
          <div className={localStyles['tooltip-container']}>
            {option.permissions.map((permission) => {
              return (
                <div className={localStyles.tooltip} key={permission}>
                  {permission}
                </div>
              );
            })}
          </div>
        }
      >
        <TipIcon className={localStyles.icon} />
      </AppTooltip>
    </div>
  );
};
